import { produce } from 'immer';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { atomFamily, useRecoilState } from 'recoil';
import { setDeep } from '../../../utils/object';
import { ChartEntity } from '../../chart/types';
import { ChartOptionsV2 } from '../../chart/types-v2';
import { DefaultBarChartUiOptions } from '../../chart/utils/echart';
import { SelectChartByIdState, updateChart } from '../ChartsState';

export type YCol = {
  colName: string;
  customLabel?: string;
  valueType: string;
  chartType: string;
  color?: string;
};

export type AreaChartUiOptions = {
  version?: number;
  chartOptions?: {
    showLegend?: boolean;
    enableStacking?: boolean;
    normalizeData?: boolean;
  };
  xAxisOptions?: {
    label?: string;
    tickFormat?: string;
    logarithmic?: boolean;
    sortValues?: boolean;
    reverseValue?: boolean;
  };
  yAxisOptions?: {
    label?: string;
    tickFormat?: string;
    labelFormat?: string;
    logarithmic?: boolean;
    enableRightYAxis?: boolean;
  };
  dataOptions?: {
    groupByCol?: string;
    xCol?: {
      colName: string;
      customLabel?: string;
      valueType: string;
    };
    yCols?: YCol[];
    groupByYCols?: YCol[];
  };
};

export type SetAreaChartOptions = (n: ChartOptionsV2) => void;

export const AreaChartOptionsState = atomFamily<ChartOptionsV2, string>({
  key: 'AreaChartOptionsState',
  default: {
    displayName: 'Area chart',
    uiOptions: DefaultBarChartUiOptions,
  },
});

export function useAreaChartOptions(chartId: string) {
  const [chart, setChart] = useRecoilState(
    SelectChartByIdState({ id: chartId })
  );
  const [areaChartOptions, setAreaChartOptionsState] = useRecoilState(
    AreaChartOptionsState(chartId)
  );

  const [updating, setUpdating] = useState(false);

  const debouncedEventHandler = useMemo(
    () =>
      debounce((nAreaChartOptions: ChartOptionsV2) => {
        // trigger rerender chart

        const nChart: ChartEntity = {
          ...chart,
          ...nAreaChartOptions,
        } as ChartEntity;

        setChart(nChart);

        // TODO: call api to update chart
        // eslint-disable-next-area
        // console.log('api update & trigger rerender chart', nChart);
        updateChart({ chart: nChart });

        setUpdating(false);
      }, 600),
    [chart, setChart]
  );

  const setChartOptions = useCallback(
    (nAreaChartOptions: ChartOptionsV2) => {
      setUpdating(true);

      // update options
      setAreaChartOptionsState(nAreaChartOptions);
      // trigger rerender by updating chart
      debouncedEventHandler(nAreaChartOptions);
    },
    [debouncedEventHandler]
  );

  const setChartOptionsByMap = useCallback(
    // eslint-disable-next-area
    (valueMap: Record<string, unknown>) => {
      setChartOptions(
        produce(areaChartOptions, (draft) => {
          setDeep(draft, valueMap);
        })
      );
    },
    [areaChartOptions, setChartOptions]
  );

  return [
    areaChartOptions,
    updating,
    {
      setChartOptions,
      setChartOptionsByMap,
    },
  ] as const;
}
